import { useMemo } from 'react';
import { usePMContext } from '../../contexts/PMContext';
import { PMStatus } from '../../types/pmTypes';
import { SingleUnit } from '../pages-equipment/equipmentTypes';
import { Btn, Card, Scrollable, Tooltip } from '../ui';
import CardHeader from '../ui/headers/CardHeader';
import { PM_SIDEBAR_ITEMS_IN_VIEW } from './pmConstants';
import '../../css/PMDashboard.css';

interface PMSidebarPanelsProps {
    onSelectUnit?: (unitId: string) => void;
}

interface GroupedUnit {
    unitId: string;
    name: string;
    category: string;
}

interface PanelConfig {
    status: PMStatus;
    label: string;
    dotClass: string;
    tooltip?: string;
}

const PANELS: PanelConfig[] = [
    {
        status: PMStatus.OVERDUE,
        label: 'Overdue',
        dotClass: 'overdue',
        tooltip: 'The current reading is at or past the next service target.',
    },
    {
        status: PMStatus.DUE_SOON,
        label: 'Due Soon',
        dotClass: 'due-soon',
        tooltip: 'The current reading is inside the warning window before the next service target.',
    },
    { status: PMStatus.SCHEDULED, label: 'Scheduled / Upcoming', dotClass: 'scheduled' },
];

/**
 * Three sidebar panels grouping enrolled equipment by PM status:
 * Overdue (red), Due Soon (yellow), Scheduled/Upcoming (green).
 *
 * Validates: Requirement 7.5
 */
export const PMSidebarPanels = ({ onSelectUnit }: PMSidebarPanelsProps) => {
    const { enrolledUnits, getStatusForUnit } = usePMContext();

    const groupedUnits = useMemo(() => {
        const groups: Record<string, GroupedUnit[]> = {
            [PMStatus.OVERDUE]: [],
            [PMStatus.DUE_SOON]: [],
            [PMStatus.SCHEDULED]: [],
        };

        for (const unit of enrolledUnits as SingleUnit[]) {
            const status = getStatusForUnit(unit.id);
            const groupKey = status === PMStatus.COMPLETED ? PMStatus.SCHEDULED : status;

            if (groups[groupKey]) {
                groups[groupKey].push({
                    unitId: unit.id,
                    name: unit.name ?? 'Unknown',
                    category: unit.category?.name ?? '',
                });
            }
        }

        return groups;
    }, [enrolledUnits, getStatusForUnit]);

    return (
        <div className="pm-sidebar">
            {PANELS.map((panel) => {
                const items = groupedUnits[panel.status] ?? [];

                return (
                    <Card key={panel.status} variant="card-sm" className="pm-sidebar-panel">
                        <CardHeader
                            as="h3"
                            title={
                                panel.tooltip ? (
                                    <Tooltip content={panel.tooltip}>
                                        <span className="pm-sidebar-panel-title">
                                            <span className={`pm-sidebar-dot ${panel.dotClass}`} />
                                            {panel.label}
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <span className="pm-sidebar-panel-title">
                                        <span className={`pm-sidebar-dot ${panel.dotClass}`} />
                                        {panel.label}
                                    </span>
                                )
                            }
                        />
                        <div className="pm-sidebar-col-header">Equipment</div>
                        {items.length === 0 ? (
                            <div className="pm-sidebar-empty">No equipment in this category</div>
                        ) : (
                            <Scrollable
                                className="pm-sidebar-list"
                                variant="vertical"
                                itemsInView={PM_SIDEBAR_ITEMS_IN_VIEW}
                            >
                                {items.map((item) => (
                                    <div key={item.unitId}>
                                        <Btn
                                            variant="ghost"
                                            buttonClasses="pm-sidebar-item"
                                            buttonText={item.category ? `${item.name} – ${item.category}` : item.name}
                                            buttonOnClick={() => onSelectUnit?.(item.unitId)}
                                        />
                                    </div>
                                ))}
                            </Scrollable>
                        )}
                    </Card>
                );
            })}
        </div>
    );
};
